import { useEffect, useState, useCallback } from "react";
import { useHub } from "../../context/HubContext";
import { getHubEvents } from "../../services/eventService";
import { getHubEventBreakdown } from "../../services/statisticsService";
import PageHeader from "../../components/common/PageHeader";
import EventCard from "../../components/ingroove/events/EventCard";
import { Search, ChevronLeft, ChevronRight, Calendar } from "lucide-react";

const statusFilters = [
  { label: "All", value: "" },
  { label: "Upcoming", value: 0 },
  { label: "Live", value: 1 },
  { label: "Past", value: 2 },
];

const Events = () => {
  const { hub } = useHub();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ totalRecords: 0, totalPages: 0 });
  const [statusFilter, setStatusFilter] = useState("");

  const fetchEvents = useCallback(
    async (searchVal, pageVal) => {
      if (!hub?.id) return;
      setLoading(true);
      try {
        const [eventsRes, breakdownRes] = await Promise.all([
          getHubEvents(hub.id, {
            search: searchVal,
            pageNumber: pageVal,
            pageSize: 9,
          }),
          getHubEventBreakdown(hub.id),
        ]);

        const salesMap = {};
        breakdownRes.events.forEach((e) => {
          if (e?.eventId) salesMap[e.eventId] = e;
        });

        const enriched = eventsRes.data.filter(Boolean).map((event) => ({
          ...event,
          salesStats: salesMap[event.id] ?? null,
        }));

        const filtered =
          statusFilter !== ""
            ? enriched.filter((e) => e?.status === statusFilter)
            : enriched;

        setEvents(filtered);
        setMeta({
          totalRecords: eventsRes.totalRecords,
          totalPages: eventsRes.totalPages,
        });
      } finally {
        setLoading(false);
      }
    },
    [hub?.id, statusFilter],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchEvents(search, 1);
    }, 400);
    return () => clearTimeout(t);
  }, [search, statusFilter, hub?.id]);

  useEffect(() => {
    fetchEvents(search, page);
  }, [page]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Events"
        subtitle={`${meta.totalRecords.toLocaleString()} events`}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            type="text"
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-xl text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
          />
        </div>

        <div className="flex gap-1 bg-bg-surface border border-border rounded-xl p-1">
          {statusFilters.map((s) => (
            <button
              key={s.label}
              onClick={() => {
                setStatusFilter(s.value);
                setPage(1);
              }}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                statusFilter === s.value
                  ? "ingroove-gradient-bg text-white shadow-glow"
                  : "text-text-muted hover:text-text-secondary"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-bg-surface border border-border rounded-2xl h-64 animate-pulse"
            />
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Calendar size={32} className="text-border-hover mb-3" />
          <p className="text-sm text-text-muted">No events found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>
            Page {page} of {meta.totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-xl border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page === meta.totalPages}
              className="p-1.5 rounded-xl border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
